import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Sparkles,
  BookOpen,
  ScrollText,
  Compass,
  Layers,
  ChevronDown,
  ArrowUp,
  Volume1,
  Sliders,
  X,
  Headphones,
  Highlighter,
  Video,
  LocateFixed,
  Radio,
} from 'lucide-react';
import { PageData, TOTAL_PAGES } from '../../types/tome';
import { getPage, getActs, getChapters } from '../../data/tome';
import { getAudioEngine } from '../../audio';
import {
  getNarrationController,
  loadAIConfig,
  saveAIConfig,
  CHARACTER_VOICE_PRESETS,
  GoogleGenAIConfig,
} from '../../services/googleGenAI';
import {
  getVisualForPage,
  CHARACTER_PORTRAITS,
  VisualEntry,
} from '../../services/visualRegistry';
import { formatPageParagraphs, FormattedParagraph, isTextBeingSpoken } from '../../utils/proseFormatter';
import { AudioVisualizer } from '../Audio/AudioVisualizer';
import { VoiceStudioModal } from '../Audio/VoiceStudioModal';
import { AnimatedChapterSplash } from './AnimatedChapterSplash';
import { VideoMotionCanvas } from '../Artwork/VideoMotionCanvas';

export interface InlineStoryReaderProps {
  initialPage?: number;
  onPageChange?: (pageNum: number) => void;
  onSwitchToTome?: () => void;
  onOpenCodex?: () => void;
  onOpenAiSuite?: () => void;
}

const BATCH_SIZE = 15;

export const InlineStoryReader: React.FC<InlineStoryReaderProps> = ({
  initialPage = 1,
  onPageChange,
  onSwitchToTome,
  onOpenCodex,
  onOpenAiSuite,
}) => {
  const [activePageNum, setActivePageNum] = useState<number>(initialPage);
  const [renderedPages, setRenderedPages] = useState<PageData[]>(() => {
    const start = Math.max(1, initialPage - 2);
    const end = Math.min(TOTAL_PAGES, start + BATCH_SIZE);
    const pages: PageData[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(getPage(p));
    }
    return pages;
  });

  const [aiConfig, setAiConfig] = useState<GoogleGenAIConfig>(loadAIConfig);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingText, setSpeakingText] = useState<string>('');
  const [speakingPage, setSpeakingPage] = useState<number | null>(null);
  const [speakingParaIndex, setSpeakingParaIndex] = useState<number | null>(null);
  const [speakingWordIndex, setSpeakingWordIndex] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [ambientVolume, setAmbientVolume] = useState(0.55);

  // Modals & Panels
  const [isVoiceStudioOpen, setIsVoiceStudioOpen] = useState(false);
  const [isChapterDrawerOpen, setIsChapterDrawerOpen] = useState(false);
  const [isDarkDeckOpen, setIsDarkDeckOpen] = useState(false);
  const [lightboxVisual, setLightboxVisual] = useState<VisualEntry | null>(null);
  const [isInlineVideoMode, setIsInlineVideoMode] = useState(true);

  // Dark Ambience State
  const [darkPreset, setDarkPreset] = useState<string>('geryon_keel');
  const [darkDroneBoost, setDarkDroneBoost] = useState(0.75);
  const [subBassRumble, setSubBassRumble] = useState(0.85);
  const [cosmicHiss, setCosmicHiss] = useState(0.35);

  const [jumpPageInput, setJumpPageInput] = useState<string>('');

  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRefs = useRef<Map<number, HTMLElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const audioEngine = useRef(getAudioEngine()).current;
  const narration = useRef(getNarrationController()).current;

  // Subscribe to narration controller state with real-time per-word tracking
  useEffect(() => {
    const unsub = narration.subscribe((speaking, _paused, currentText, pageNum, paraIdx, wordIdx) => {
      setIsSpeaking(speaking);
      setSpeakingText(currentText || '');
      setSpeakingPage(pageNum ?? null);
      setSpeakingParaIndex(paraIdx ?? null);
      setSpeakingWordIndex(wordIdx ?? null);
    });
    return unsub;
  }, [narration]);

  // Initial Dark Ambience calibration
  useEffect(() => {
    try {
      audioEngine.setDarkAtmosphere({
        darkDroneBoost: 0.75,
        subBassRumble: 0.85,
        cosmicHiss: 0.35,
      });
    } catch {}
  }, [audioEngine]);

  // Setup IntersectionObserver for auto-updating active page & ambient audio
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        let bestEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
              bestEntry = entry;
            }
          }
        }

        if (bestEntry && bestEntry.target instanceof HTMLElement) {
          const pageNum = parseInt(bestEntry.target.dataset.pageNumber || '0', 10);
          if (pageNum >= 1 && pageNum <= TOTAL_PAGES && pageNum !== activePageNum) {
            setActivePageNum(pageNum);
            onPageChange?.(pageNum);

            try {
              const pData = getPage(pageNum);
              audioEngine.updateAudioCue(pData.audio);
            } catch {
              // ignore
            }
          }
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -20% 0px',
        threshold: [0.1, 0.3, 0.5, 0.7],
      }
    );

    observerRef.current = observer;

    sceneRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [renderedPages, activePageNum, onPageChange, audioEngine]);

  // Infinite scroll loader: append more pages when approaching bottom
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollHeight - (scrollTop + clientHeight) < 1600) {
      setRenderedPages((prev) => {
        const lastPage = prev[prev.length - 1]?.pageNumber || 0;
        if (lastPage >= TOTAL_PAGES) return prev;
        const nextBatch: PageData[] = [];
        const target = Math.min(TOTAL_PAGES, lastPage + BATCH_SIZE);
        for (let p = lastPage + 1; p <= target; p++) {
          nextBatch.push(getPage(p));
        }
        return [...prev, ...nextBatch];
      });
    }
  }, []);

  // Jump to specific page
  const scrollToPage = useCallback(
    (targetPage: number) => {
      const pageClamped = Math.max(1, Math.min(TOTAL_PAGES, targetPage));

      const isAlreadyRendered = renderedPages.some((p) => p.pageNumber === pageClamped);
      if (!isAlreadyRendered) {
        const start = Math.max(1, pageClamped - 2);
        const end = Math.min(TOTAL_PAGES, pageClamped + BATCH_SIZE);
        const pages: PageData[] = [];
        for (let p = start; p <= end; p++) {
          pages.push(getPage(p));
        }
        setRenderedPages(pages);
      }

      setActivePageNum(pageClamped);
      onPageChange?.(pageClamped);

      setTimeout(() => {
        const el = sceneRefs.current.get(pageClamped);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 70);
    },
    [renderedPages, onPageChange]
  );

  // Play continuous narration across the entire novel starting from pageNum
  const handleToggleNarration = (pageNum: number = activePageNum) => {
    if (isSpeaking) {
      narration.stop();
    } else {
      narration.narrateContinuously(pageNum, aiConfig);
    }
  };

  // 1-Click Jump to where the audio narrator is currently reading
  const handleJumpToNarrator = () => {
    if (speakingPage !== null) {
      scrollToPage(speakingPage);
      setTimeout(() => {
        const paraEl =
          document.querySelector(
            `[data-page-number="${speakingPage}"] [data-para-index="${speakingParaIndex}"]`
          ) || document.querySelector(`[data-page-number="${speakingPage}"]`);
        if (paraEl) {
          paraEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 120);
    }
  };

  // Toggle synchronized read-along text highlighter
  const handleToggleHighlight = () => {
    const nextVal = aiConfig.enableReadAlongHighlight === false ? true : false;
    const updated = { ...aiConfig, enableReadAlongHighlight: nextVal };
    setAiConfig(updated);
    saveAIConfig(updated);
  };

  // Set of expanded clinical dossiers / marginalia
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const toggleLogExpansion = (logKey: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(logKey)) next.delete(logKey);
      else next.add(logKey);
      return next;
    });
  };

  // Click-to-Speak starting directly at any specific paragraph or word in character voice
  const handleSpeakFromPoint = (
    pageNum: number,
    paraIndex: number,
    characterId?: string,
    wordIndex?: number
  ) => {
    try {
      narration.narrateFromParagraph(
        pageNum,
        paraIndex,
        aiConfig,
        characterId,
        true, // continuous mode: seamless page-advancing narration!
        wordIndex
      );
    } catch (err) {
      console.warn('[Narration] Failed to start from point:', err);
    }
  };

  // Toggle ambient soundscape
  const handleToggleMute = () => {
    const nextMute = !isMuted;
    setIsMuted(nextMute);
    if (nextMute) {
      audioEngine.setMasterVolume(0);
    } else {
      audioEngine.setMasterVolume(ambientVolume);
    }
  };

  const handleVolumeChange = (v: number) => {
    setAmbientVolume(v);
    if (!isMuted) {
      audioEngine.setMasterVolume(v);
    }
  };

  // Dark Ambience Presets
  const handleApplyDarkPreset = (
    preset: 'geryon_keel' | 'deep_margin' | 'planck_ignition' | 'obsidian_ledger'
  ) => {
    setDarkPreset(preset);
    audioEngine.applyAtmospherePreset(preset);
  };

  const handleDarkSliderChange = (
    type: 'drone' | 'sub' | 'noise',
    val: number
  ) => {
    if (type === 'drone') setDarkDroneBoost(val);
    if (type === 'sub') setSubBassRumble(val);
    if (type === 'noise') setCosmicHiss(val);

    audioEngine.setDarkAtmosphere({
      darkDroneBoost: type === 'drone' ? val : darkDroneBoost,
      subBassRumble: type === 'sub' ? val : subBassRumble,
      cosmicHiss: type === 'noise' ? val : cosmicHiss,
    });
  };

  const activePageData = getPage(activePageNum);
  const acts = getActs();
  const chapters = getChapters();
  const currentVoicePreset =
    CHARACTER_VOICE_PRESETS[aiConfig.characterPreset || 'marcus_ward'] ||
    CHARACTER_VOICE_PRESETS.marcus_ward;

  return (
    <div className="inline-story-reader-root" data-testid="inline-story-reader">
      {/* Floating Jump to Narrator Pill when voice is on a different page */}
      {isSpeaking && speakingPage !== null && speakingPage !== activePageNum && (
        <button
          onClick={handleJumpToNarrator}
          className="floating-narrator-tracker-pill"
          title={`Narration is actively speaking Page ${speakingPage}. Click to jump directly to voice.`}
        >
          <span className="tracker-pulse-dot" />
          <Radio size={14} className="tracker-radio-icon" />
          <span>NARRATOR READING ON PAGE {speakingPage} — CLICK TO JUMP</span>
        </button>
      )}

      {/* 1. Continuous Vertical Scroll Narrative Container */}
      <div
        className="inline-scroll-stream"
        ref={containerRef}
        onScroll={handleScroll}
        tabIndex={0}
      >
        {/* Atmospheric Mission Curtain */}
        <header className="inline-story-curtain">
          <div className="curtain-badge">
            <span className="curtain-live-dot" />
            <span>CONTINUOUS LITERARY SCROLL // THE CAESURA ENGINE</span>
          </div>
          <h1 className="curtain-title">THE SILENCE BETWEEN THE SECONDS</h1>
          <p className="curtain-subtitle">
            A Dark Sci-Fi Psychological Novel of Spacetime Shear, Cognitive Decay, and the Obsidian Ledger
          </p>
          <div className="curtain-author-badge">
            <span className="author-label">AUTHORED BY</span>
            <span className="author-name">ALEX HARPER</span>
            <span className="author-sep">//</span>
            <span className="author-wordcount">150,000+ WORDS</span>
          </div>

          <div className="curtain-controls-strip">
            <button
              onClick={() => handleToggleNarration(1)}
              className={`curtain-play-btn ${isSpeaking ? 'active-speaking' : ''}`}
            >
              {isSpeaking ? <Pause size={16} /> : <Play size={16} />}
              <span>{isSpeaking ? 'STOP NARRATION' : 'STREAM GOOGLE NARRATION'}</span>
            </button>

            <button
              onClick={() => setIsVoiceStudioOpen(true)}
              className="curtain-studio-btn"
              title="Calibrate pitch, chest resonance, helmet comms, and character presets"
            >
              <Sliders size={14} />
              <span>VOICE STUDIO ({currentVoicePreset.name.toUpperCase()})</span>
            </button>

            <button
              onClick={() => setIsDarkDeckOpen(!isDarkDeckOpen)}
              className="curtain-mode-btn"
              title="Tune background dark sub-bass, reactor drones, and cosmic hiss"
            >
              <Headphones size={14} />
              <span>DARK SOUNDS</span>
            </button>

            {onSwitchToTome && (
              <button onClick={onSwitchToTome} className="curtain-mode-btn">
                <BookOpen size={14} />
                <span>SWITCH TO TOME VIEW</span>
              </button>
            )}

            {onOpenAiSuite && (
              <button onClick={onOpenAiSuite} className="curtain-mode-btn" title="Configure AI Suite">
                <Sparkles size={14} />
                <span>AI SUITE</span>
              </button>
            )}
          </div>
        </header>

        {/* Dynamic Continuous Scenes Stream */}
        {renderedPages.map((page) => {
          const visual = getVisualForPage(page.pageNumber);
          const paragraphs = formatPageParagraphs(page.text, page.speaker);
          const isActiveScene = page.pageNumber === activePageNum;
          const isHeroScene = page.pageNumber === 1 || page.pageNumber === 11 || page.pageNumber % 25 === 0;
          const chapterDef = chapters.find((c) => c.startPage === page.pageNumber);

          return (
            <React.Fragment key={page.pageNumber}>
              {/* Monumental Animated Chapter Splash on Chapter Start */}
              {chapterDef && (
                <AnimatedChapterSplash
                  chapter={chapterDef}
                  page={page}
                  visual={visual}
                  onScrollIntoChapter={() => scrollToPage(page.pageNumber)}
                />
              )}

              <section
                data-page-number={page.pageNumber}
                ref={(el) => {
                  if (el) sceneRefs.current.set(page.pageNumber, el);
                  else sceneRefs.current.delete(page.pageNumber);
                }}
                className={`inline-scene-block ${isActiveScene ? 'scene-in-focus' : ''} ${
                  isHeroScene ? 'scene-hero-layout' : 'scene-standard-layout'
                }`}
              >
                {/* 1. MINIMAL SCENE INDICATOR (Ultra-clean, minimalist design) */}
                <div className="scene-minimal-marker">
                  <div className="scene-minimal-left">
                    <span className="minimal-act-label">
                      {page.actNumber === 0
                        ? 'PROLOGUE'
                        : page.actNumber === 11
                        ? 'EPILOGUE'
                        : `ACT ${page.actNumber}`}
                    </span>
                    <span className="minimal-marker-sep">•</span>
                    <span className="minimal-chapter-label">{page.chapterTitle}</span>
                    <span className="minimal-marker-sep">•</span>
                    <span className="minimal-page-label">PAGE {page.pageNumber}</span>
                  </div>
                  <div className="scene-minimal-right">
                    <button
                      onClick={() => handleToggleNarration(page.pageNumber)}
                      className={`scene-minimal-narrate-btn ${isSpeaking && isActiveScene ? 'speaking-active' : ''}`}
                      title={isSpeaking && isActiveScene ? 'Stop narration' : 'Narrate scene in full character voices'}
                    >
                      <Volume2 size={12} />
                      <span>{isSpeaking && isActiveScene ? 'STOP' : 'NARRATE'}</span>
                    </button>
                  </div>
                </div>

                {/* 2. CINEMATIC ARTWORK VISTA (Generative Video Motion Canvas or Pure Concept Painting) */}
                <div
                  className="scene-art-banner-wrapper"
                  onClick={() => !isInlineVideoMode && setLightboxVisual(visual)}
                  title={isInlineVideoMode ? 'AI Generative Video Motion Active' : 'Click to expand high-resolution concept painting'}
                >
                  {isInlineVideoMode ? (
                    <VideoMotionCanvas
                      imageUrl={visual.heroImage || '/art/planck_ignition_event.jpg'}
                      title={visual.title}
                      caption={visual.caption}
                      accentColor={visual.accentColor || '#00f3ff'}
                      shaderTheme={visual.style}
                      isCharacter={!!visual.characterId}
                      onExpand={() => setLightboxVisual(visual)}
                    />
                  ) : (
                    <div className="scene-art-image-container">
                      <img
                        src={visual.heroImage || '/art/planck_ignition_event.jpg'}
                        alt={visual.title}
                        className="scene-art-pan-image"
                        loading="lazy"
                      />
                      {/* Subtle Scanlines & Atmospheric Vignette Fade */}
                      <div className="scene-art-scanlines" />
                      <div className="scene-art-void-fade" />
                    </div>
                  )}
                </div>

                {/* 3. HIGH-CONTRAST OBSIDIAN PROSE STREAM (Layered with frosted blur over feathered art) */}
                <div className="scene-prose-stream">
                  <div className="scene-prose-header">
                    <h2 className="scene-prose-title">{page.title}</h2>
                  </div>
                {paragraphs.map((para: FormattedParagraph, pIdx: number) => {
                  const isBeingReadAloud =
                    isSpeaking &&
                    aiConfig.enableReadAlongHighlight !== false &&
                    ((speakingPage === page.pageNumber && speakingParaIndex === pIdx) ||
                      isTextBeingSpoken(speakingText, para.text));

                  // In-Character Dialogue Block
                  if (para.type === 'dialogue') {
                    const charPortrait =
                      (para.characterId && CHARACTER_PORTRAITS[para.characterId as keyof typeof CHARACTER_PORTRAITS]) ||
                      '/art/marcus_ward_portrait.jpg';
                    const words = para.text.split(/(\s+)/);

                    return (
                      <div
                        key={pIdx}
                        data-para-index={pIdx}
                        className={`inline-dialogue-dossier ${isBeingReadAloud ? 'reading-aloud-active dialogue-reading-active' : ''}`}
                        style={{ borderLeftColor: para.accentColor || '#00f3ff' }}
                        onClick={() => handleSpeakFromPoint(page.pageNumber, pIdx, para.characterId, 0)}
                        title={`Click to hear ${para.speaker || 'character'} speak from this line`}
                      >
                        {/* Character Hex Avatar Frame */}
                        <div className="dialogue-avatar-dossier-col">
                          <div
                            className="dialogue-hex-frame"
                            style={{ borderColor: para.accentColor || '#00f3ff' }}
                          >
                            <img
                              src={charPortrait}
                              alt={para.speaker || 'Character'}
                              className="dialogue-avatar-img"
                            />
                          </div>
                          <span
                            className={`dialogue-biometric-dot ${isBeingReadAloud ? 'biometric-dot-pulse' : ''}`}
                            style={{ background: para.accentColor || '#00f3ff' }}
                          />
                        </div>

                        {/* Dialogue Speech & Telemetry */}
                        <div className="dialogue-body-col">
                          <div className="dialogue-header">
                            <span
                              className="dialogue-speaker-name"
                              style={{ color: para.accentColor || '#00f3ff' }}
                            >
                              {para.speaker?.toUpperCase()}
                            </span>
                            {para.speakerRole && (
                              <span className="dialogue-speaker-role">
                                // {para.speakerRole}
                              </span>
                            )}
                            {para.emotion && (
                              <span
                                className="dialogue-emotion-badge"
                                style={{
                                  color: para.emotionColor || '#ff4081',
                                  borderColor: `${para.emotionColor || '#ff4081'}55`,
                                  backgroundColor: `${para.emotionColor || '#ff4081'}15`,
                                }}
                                title={`Biometric Vocal State: ${para.emotion}`}
                              >
                                <span
                                  className="emotion-pulse-dot"
                                  style={{ backgroundColor: para.emotionColor || '#ff4081' }}
                                />
                                <span className="emotion-tag-text">{para.emotion}</span>
                              </span>
                            )}
                            {isBeingReadAloud && (
                              <span className="reading-telemetry-tag">TRANSMITTING</span>
                            )}
                          </div>
                          <p className="dialogue-speech-text">
                            “
                            {words.map((chunk, wIdx) => {
                              if (/^\s+$/.test(chunk)) return chunk;
                              const wordIndex = Math.floor(wIdx / 2);
                              const isWordActive = isBeingReadAloud && speakingWordIndex === wordIndex;
                              const isWordPast = isBeingReadAloud && speakingWordIndex !== null && wordIndex < speakingWordIndex;
                              return (
                                <span
                                  key={wIdx}
                                  className={`interactive-word ${isWordActive ? 'word-speaking-active' : ''} ${isWordPast ? 'word-speaking-past' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSpeakFromPoint(page.pageNumber, pIdx, para.characterId, wordIndex);
                                  }}
                                  title={`Click to speak from "${chunk}" in ${para.speaker || 'character'}'s voice`}
                                >
                                  {chunk}
                                </span>
                              );
                            })}
                            ”
                          </p>
                        </div>
                      </div>
                    );
                  }

                  // Interactive Side Dossier / Tactical Marginalia
                  if (para.type === 'marginalia') {
                    const logKey = `${page.pageNumber}-${pIdx}`;
                    const isExpanded = expandedLogs.has(logKey);

                    return (
                      <div
                        key={pIdx}
                        data-para-index={pIdx}
                        className={`inline-side-dossier-pill ${isBeingReadAloud ? 'reading-aloud-active' : ''} ${isExpanded ? 'dossier-expanded' : ''}`}
                      >
                        <div
                          className="side-dossier-summary-row"
                          onClick={() => toggleLogExpansion(logKey)}
                        >
                          <div className="side-dossier-left">
                            <Radio size={12} className="side-dossier-icon" />
                            <span className="side-dossier-tag">{para.speaker || 'CLINICAL DOSSIER'}</span>
                            <span className="side-dossier-sep">//</span>
                            <span className="side-dossier-sub">{page.timestamp || 'DIAGNOSTIC LOG'}</span>
                            {isBeingReadAloud && (
                              <span className="reading-telemetry-tag">ACTIVE READOUT</span>
                            )}
                          </div>
                          <div className="side-dossier-actions">
                            <button
                              type="button"
                              className="side-dossier-listen-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSpeakFromPoint(page.pageNumber, pIdx, 'dr_simon_peter', 0);
                              }}
                              title="Listen to this clinical log in character voice"
                            >
                              <Volume2 size={11} />
                              <span>LISTEN</span>
                            </button>
                            <span className="side-dossier-expand-toggle">
                              {isExpanded ? 'COLLAPSE' : 'EXPAND LOG'}
                            </span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="side-dossier-body">
                            <p className="side-dossier-text">{para.text}</p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Rich Literary Narrative
                  const narrativeWords = para.text.split(/(\s+)/);

                  return (
                    <p
                      key={pIdx}
                      data-para-index={pIdx}
                      className={`inline-narrative-paragraph ${isBeingReadAloud ? 'reading-aloud-active' : ''}`}
                      onClick={() => handleSpeakFromPoint(page.pageNumber, pIdx, 'narrator', 0)}
                      title="Click anywhere to hear Elder Narrator continue from here"
                    >
                      {narrativeWords.map((chunk, wIdx) => {
                        if (/^\s+$/.test(chunk)) return chunk;
                        const wordIndex = Math.floor(wIdx / 2);
                        const isWordActive = isBeingReadAloud && speakingWordIndex === wordIndex;
                        const isWordPast = isBeingReadAloud && speakingWordIndex !== null && wordIndex < speakingWordIndex;
                        return (
                          <span
                            key={wIdx}
                            className={`interactive-word ${isWordActive ? 'word-speaking-active' : ''} ${isWordPast ? 'word-speaking-past' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSpeakFromPoint(page.pageNumber, pIdx, 'narrator', wordIndex);
                            }}
                            title={`Click to speak from "${chunk}" in Elder Narrator's voice`}
                          >
                            {chunk}
                          </span>
                        );
                      })}
                      {isBeingReadAloud && (
                        <span className="reading-telemetry-tag">VOX</span>
                      )}
                    </p>
                  );
                })}
              </div>

              {/* Elegant Scene Transition Divider */}
              <div className="scene-transition-divider">
                <span className="divider-line" />
                <span className="divider-cipher">
                  ✦ DISCRETE TIME-STEP {page.pageNumber} // {page.chapterTitle} ✦
                </span>
                <span className="divider-line" />
              </div>
            </section>
          </React.Fragment>
        );
        })}

        {/* Scroll Sentinel */}
        <div className="inline-scroll-sentinel">
          <span className="sentinel-dot" />
          <span className="sentinel-label">
            {renderedPages[renderedPages.length - 1]?.pageNumber >= TOTAL_PAGES
              ? '— END OF RECORD // THE OBSIDIAN LEDGER RESTS —'
              : 'COMPUTING SUBSEQUENT PLANCK COORDINATES...'}
          </span>
        </div>
      </div>

      {/* 2. Right Chrono-Track Navigation Rail */}
      <aside className="chrono-track-rail" aria-label="Acts and Chapters Chrono-Track">
        <div className="chrono-rail-inner">
          {acts.slice(0, 10).map((act) => {
            const isActActive = activePageData.actNumber === act.actNumber;
            return (
              <button
                key={act.actNumber}
                onClick={() => scrollToPage(act.startPage)}
                className={`chrono-rail-tick ${isActActive ? 'tick-active' : ''}`}
                title={`Act ${act.actNumber}: ${act.title} (Page ${act.startPage})`}
              >
                <span className="tick-dot" />
                <span className="tick-label">A{act.actNumber}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* 3. Dark Ambience Drawer / Deck */}
      {isDarkDeckOpen && (
        <div className="dark-ambience-panel">
          <div className="dark-ambience-header">
            <div className="dark-title-group">
              <Headphones size={15} className="dark-icon" />
              <span>DARK AMBIENT SOUND GENERATOR</span>
            </div>
            <button onClick={() => setIsDarkDeckOpen(false)} className="dark-close-btn">
              <X size={14} />
            </button>
          </div>

          <div className="dark-presets-row">
            <button
              onClick={() => handleApplyDarkPreset('geryon_keel')}
              className={`dark-preset-pill ${darkPreset === 'geryon_keel' ? 'pill-active' : ''}`}
            >
              GERYON KEEL (24Hz RUMBLE)
            </button>
            <button
              onClick={() => handleApplyDarkPreset('deep_margin')}
              className={`dark-preset-pill ${darkPreset === 'deep_margin' ? 'pill-active' : ''}`}
            >
              VOID MARGIN (COLD WIND)
            </button>
            <button
              onClick={() => handleApplyDarkPreset('planck_ignition')}
              className={`dark-preset-pill ${darkPreset === 'planck_ignition' ? 'pill-active' : ''}`}
            >
              PLANCK SHEAR (TENSE BINAURAL)
            </button>
            <button
              onClick={() => handleApplyDarkPreset('obsidian_ledger')}
              className={`dark-preset-pill ${darkPreset === 'obsidian_ledger' ? 'pill-active' : ''}`}
            >
              OBSIDIAN LEDGER (DREAD)
            </button>
          </div>

          <div className="dark-sliders-row">
            <div className="dark-slider-item">
              <span>SUB-BASS REACTOR RUMBLE</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={subBassRumble}
                onChange={(e) => handleDarkSliderChange('sub', parseFloat(e.target.value))}
                className="dark-fader"
              />
            </div>
            <div className="dark-slider-item">
              <span>DARK HARMONIC DRONE</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={darkDroneBoost}
                onChange={(e) => handleDarkSliderChange('drone', parseFloat(e.target.value))}
                className="dark-fader"
              />
            </div>
            <div className="dark-slider-item">
              <span>COSMIC VACUUM DRIFT</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={cosmicHiss}
                onChange={(e) => handleDarkSliderChange('noise', parseFloat(e.target.value))}
                className="dark-fader"
              />
            </div>
          </div>
        </div>
      )}

      {/* 4. Floating Sticky Director HUD */}
      <footer className="inline-director-hud" aria-label="Director & Voice Narration Controls">
        <div className="director-hud-inner">
          {/* Left: Active Scene & Navigation */}
          <div className="hud-scene-info">
            <button
              onClick={() => setIsChapterDrawerOpen(!isChapterDrawerOpen)}
              className="hud-chapter-trigger-btn"
              title="Open Chapter Index"
            >
              <Compass size={14} className="hud-compass-icon" />
              <div className="hud-chapter-text-group">
                <span className="hud-act-label">
                  PAGE {activePageNum} OF {TOTAL_PAGES}
                </span>
                <span className="hud-chapter-title">
                  {activePageData.chapterTitle}
                </span>
              </div>
              <ChevronDown size={14} />
            </button>
          </div>

          {/* Center: Live Google Voice Stream Controller & AudioVisualizer */}
          <div className="hud-voice-center">
            <button
              onClick={() => handleToggleNarration(activePageNum)}
              className={`hud-main-voice-btn ${isSpeaking ? 'btn-active-speaking' : ''}`}
              title="Stream continuous voice narration through the entire novel"
            >
              {isSpeaking ? <Square size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
              <span>{isSpeaking ? 'STOP' : 'STREAM NARRATION'}</span>
            </button>

            {/* 1-Click Jump to Current Voice Location */}
            {isSpeaking && speakingPage !== null && (
              <button
                onClick={handleJumpToNarrator}
                className={`hud-jump-narrator-btn ${speakingPage !== activePageNum ? 'pulse-attention' : ''}`}
                title={`Jump directly to active voice reading on Page ${speakingPage}`}
              >
                <LocateFixed size={13} className="jump-icon" />
                <span>JUMP TO VOICE (P.{speakingPage})</span>
              </button>
            )}

            {/* Real-Time FFT Spectrum Visualizer Canvas */}
            <div className="hud-visualizer-wrapper">
              <AudioVisualizer isSpeaking={isSpeaking} width={130} height={22} />
            </div>

            <div className="hud-voice-telemetry">
              <span className="hud-voice-badge">
                COMM-RACK: {currentVoicePreset.name.toUpperCase()} // 24kHz DSP
              </span>
              {speakingText && (
                <span className="hud-speaking-transcript" title={speakingText}>
                  “{speakingText.slice(0, 55)}...”
                </span>
              )}
            </div>
          </div>

          {/* Right: Voice Studio Drawer, Dark Ambience & Soundscape */}
          <div className="hud-right-tools">
            {/* Read-Along Highlighter Toggle */}
            <button
              onClick={handleToggleHighlight}
              className={`hud-quick-btn hud-highlighter-btn ${aiConfig.enableReadAlongHighlight !== false ? 'active' : ''}`}
              title="Toggle synchronized read-along text highlighter"
            >
              <Highlighter size={13} />
              <span>HIGHLIGHTER {aiConfig.enableReadAlongHighlight !== false ? 'ON' : 'OFF'}</span>
            </button>

            {/* Side Logs Auto-Read vs Skip Mode Toggle */}
            <button
              onClick={() => {
                const nextMode: 'skip' | 'auto' = aiConfig.marginaliaReadMode === 'auto' ? 'skip' : 'auto';
                const updated: GoogleGenAIConfig = { ...aiConfig, marginaliaReadMode: nextMode };
                setAiConfig(updated);
                saveAIConfig(updated);
              }}
              className={`hud-quick-btn ${aiConfig.marginaliaReadMode === 'auto' ? 'active' : ''}`}
              title={
                aiConfig.marginaliaReadMode === 'auto'
                  ? 'Side Logs: Auto-read enabled'
                  : 'Side Logs: Skipped in continuous narration (click on-demand to hear)'
              }
            >
              <Radio size={13} />
              <span>{aiConfig.marginaliaReadMode === 'auto' ? 'SIDE LOGS [AUTO]' : 'SIDE LOGS [SKIP]'}</span>
            </button>

            {/* AI Video Motion Engine Toggle */}
            <button
              onClick={() => setIsInlineVideoMode(!isInlineVideoMode)}
              className={`hud-quick-btn ${isInlineVideoMode ? 'active' : ''}`}
              title="Toggle Generative Video Motion Engine (60fps Volumetric Parallax & Light Sweep)"
            >
              <Video size={13} />
              <span>AI VIDEO {isInlineVideoMode ? '[ON]' : '[OFF]'}</span>
            </button>

            {/* Voice Studio Button */}
            <button
              onClick={() => setIsVoiceStudioOpen(true)}
              className="hud-quick-btn hud-voice-studio-btn"
              title="Open Geryon Comm-Deck Vocal DSP Studio (Pitch, Pace, Chest Bass, Tube Grit)"
            >
              <Sliders size={13} />
              <span>VOICE STUDIO</span>
            </button>

            {/* Dark Ambience Toggle */}
            <button
              onClick={() => setIsDarkDeckOpen(!isDarkDeckOpen)}
              className={`hud-quick-btn ${isDarkDeckOpen ? 'active' : ''}`}
              title="Toggle Dark Ambient Background Sound Faders"
            >
              <Headphones size={13} />
              <span>DARK SOUNDS</span>
            </button>

            {/* Ambient Soundscape Volume */}
            <div className="hud-soundscape-group">
              <button
                onClick={handleToggleMute}
                className={`hud-quick-btn ${isMuted ? 'muted' : ''}`}
                title={isMuted ? 'Unmute Ambient Soundscape' : 'Mute Ambient Soundscape'}
              >
                {isMuted ? <VolumeX size={14} /> : ambientVolume < 0.5 ? <Volume1 size={14} /> : <Volume2 size={14} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : ambientVolume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="hud-volume-slider"
                title="Master Ambient Soundscape Volume"
              />
            </div>

            {/* Switch to Classic Tome Mode */}
            {onSwitchToTome && (
              <button
                onClick={onSwitchToTome}
                className="hud-mode-switch-btn"
                title="Switch to Paginated Classic Tome Mode"
              >
                <BookOpen size={14} />
                <span>TOME VIEW</span>
              </button>
            )}

            {/* Scroll to Top */}
            <button
              onClick={() => scrollToPage(1)}
              className="hud-quick-btn hud-scroll-top-btn"
              title="Scroll to Top"
            >
              <ArrowUp size={14} />
            </button>
          </div>
        </div>
      </footer>

      {/* 5. Geryon Comm-Deck Voice Studio Modal */}
      <VoiceStudioModal
        isOpen={isVoiceStudioOpen}
        onClose={() => setIsVoiceStudioOpen(false)}
        onConfigChange={(newCfg) => setAiConfig(newCfg)}
      />

      {/* 6. Full-Screen High-Resolution Artwork Lightbox */}
      {lightboxVisual && (
        <div className="art-lightbox-backdrop" onClick={() => setLightboxVisual(null)}>
          <div className="art-lightbox-card" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-top-bar">
              <div className="lightbox-title-group">
                <span className="lightbox-category-tag">{lightboxVisual.category.toUpperCase()}</span>
                <h3>{lightboxVisual.title}</h3>
              </div>
              <button onClick={() => setLightboxVisual(null)} className="lightbox-close-btn">
                <X size={18} />
              </button>
            </div>

            <div className="lightbox-image-viewport">
              <img
                src={lightboxVisual.heroImage || '/art/planck_ignition_event.jpg'}
                alt={lightboxVisual.title}
                className="lightbox-full-img"
              />
              <div className="lightbox-scanline-shimmer" />
            </div>

            <div className="lightbox-footer-info">
              <p className="lightbox-caption">{lightboxVisual.caption}</p>
              <p className="lightbox-prompt-note">
                <strong>Concept Art Direction:</strong> {lightboxVisual.prompt}
              </p>
              <div className="lightbox-footer-actions">
                <button
                  onClick={() => {
                    handleToggleNarration(activePageNum);
                    setLightboxVisual(null);
                  }}
                  className="lightbox-action-btn"
                >
                  <Volume2 size={14} />
                  <span>NARRATE THIS SCENE</span>
                </button>
                <button
                  onClick={() => {
                    setLightboxVisual(null);
                    onOpenCodex?.();
                  }}
                  className="lightbox-action-btn secondary"
                >
                  <Layers size={14} />
                  <span>VIEW IN 58+ VISUAL CODEX</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 7. Fast Jump Chapter Drawer */}
      {isChapterDrawerOpen && (
        <div className="hud-chapter-drawer-backdrop" onClick={() => setIsChapterDrawerOpen(false)}>
          <div
            className="hud-chapter-drawer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Table of Contents Drawer"
          >
            <div className="drawer-header">
              <div className="drawer-title-group">
                <ScrollText size={16} />
                <h3>CHAPTER & SCENE CHRONO-INDEX</h3>
              </div>
              <button
                onClick={() => setIsChapterDrawerOpen(false)}
                className="drawer-close-btn"
              >
                ✕
              </button>
            </div>

            {/* Direct Page Input Jump */}
            <div className="drawer-quick-jump-box">
              <span className="jump-label">JUMP TO EXACT PAGE (1-{TOTAL_PAGES}):</span>
              <div className="jump-input-row">
                <input
                  type="number"
                  min="1"
                  max={TOTAL_PAGES}
                  value={jumpPageInput}
                  onChange={(e) => setJumpPageInput(e.target.value)}
                  placeholder={`e.g. ${activePageNum}`}
                  className="jump-number-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const p = parseInt(jumpPageInput, 10);
                      if (!isNaN(p)) {
                        scrollToPage(p);
                        setIsChapterDrawerOpen(false);
                      }
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const p = parseInt(jumpPageInput, 10);
                    if (!isNaN(p)) {
                      scrollToPage(p);
                      setIsChapterDrawerOpen(false);
                    }
                  }}
                  className="jump-go-btn"
                >
                  GO
                </button>
              </div>
            </div>

            {/* Acts & Chapters List */}
            <div className="drawer-scroll-list">
              {acts.map((act) => {
                const actChapters = chapters.filter(
                  (c) => c.startPage >= act.startPage && c.endPage <= act.endPage
                );

                return (
                  <div key={act.actNumber} className="drawer-act-group">
                    <div className="drawer-act-banner">
                      <span className="act-num">
                        {act.actNumber === 0 ? 'PROLOGUE' : `ACT ${act.actNumber}`}
                      </span>
                      <span className="act-name">{act.title}</span>
                      <span className="act-range">
                        P.{act.startPage}—{act.endPage}
                      </span>
                    </div>

                    <div className="drawer-chapters-sublist">
                      {actChapters.map((ch) => (
                        <button
                          key={ch.chapterNumber}
                          onClick={() => {
                            scrollToPage(ch.startPage);
                            setIsChapterDrawerOpen(false);
                          }}
                          className={`drawer-chapter-item ${
                            activePageNum >= ch.startPage && activePageNum <= ch.endPage
                              ? 'chapter-active'
                              : ''
                          }`}
                        >
                          <span className="ch-num">CH.{ch.chapterNumber}</span>
                          <span className="ch-title">{ch.title}</span>
                          <span className="ch-page">p.{ch.startPage}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
